<template>
    <section>
        <div v-if="isEditMode" class="w-100 d-flex justify-content-end mb-3">
            <button class="btn btn-sm btn-outline-primary" @click.prevent="copyFunding()">
                Create new funding based on this template
            </button>
        </div>
        <div class="form-group row">
            <label for="source_type" class="small col-3">*Funding Source:</label>
            <select
                name="source_type"
                id="source_type"
                v-model="sourceType"
                :disabled="isEditMode"
                :class="errors['source_type'] ? 'border-danger' : ''"
                class="form-control form-control-sm col-4"
            >
                <option selected disabled hidden :value="null">Choose one</option>
                <option v-for="o in sourceTypes" :value="o.simple">{{ o.pretty }}</option>
            </select>
        </div>

        <div class="form-group row">
            <label for="source_id" class="small col-3">*Funding Provider:</label>
            <select
                name="source_id"
                id="source_id"
                v-model="funding.source_id"
                :disabled="isEditMode || !sourceType"
                :class="errors['source_id'] ? 'border-danger' : ''"
                class="form-control form-control-sm col-6"
            >
                <option selected disabled hidden :value="null">{{
                    sourceType ? "Choose one" : "Please choose a Funding Source"
                }}</option>
                <option v-for="o in filteredFundingSources" :value="o.id">{{ o.name }}</option>
            </select>
        </div>

        <div class="form-group row">
            <label for="appointment_type_id" class="small col-3">*What type of services:</label>
            <select
                name="appointment_type_id"
                id="appointment_type_id"
                v-model="contract.appointment_type_id"
                :class="errors['appointment_type_id'] ? 'border-danger' : ''"
                class="form-control form-control-sm col-6"
            >
                <option selected disabled hidden :value="null">Choose one</option>
                <option v-for="o in appointmentTypes" :value="o.id">{{ o.name }}</option>
            </select>
        </div>

        <div class="form-group row align-items-center" v-if="sourceType === 'insurance_provider'">
            <label for="diagnoses" class="small col-3">*Diagnosis codes:</label>
            <div class="col-8 px-0">
                <vw-multi-select
                    id="diagnoses"
                    v-model="contract.diagnoses"
                    :styles="{ input: { width: '100%' } }"
                    :invalid="errors['diagnoses']"
                    :template="'bootstrap'"
                    :iconset="'font-awesome'"
                    :list="diagnoses"
                    :value-property="'code'"
                    :searchable="true"
                    :tags="true"
                    :selected-list-display-function="item => item.code"
                    :list-item-display-function="item => `${item.code} - ${item.service_nickname}`"
                ></vw-multi-select>
            </div>
        </div>

        <div class="form-group row align-items-center" v-if="sourceType === 'insurance_provider'">
            <label for="cpt_codes" class="small col-3">*CPT Codes:</label>
            <div class="col-8 px-0">
                <vw-multi-select
                    id="cpt_codes"
                    v-model="contract.cpt_code_ids"
                    :invalid="errors['cpt_code_ids']"
                    :styles="{ input: { width: '100%' } }"
                    :template="'bootstrap'"
                    :iconset="'font-awesome'"
                    :list="activeCodes"
                    :value-property="'id'"
                    :searchable="true"
                    :tags="true"
                    :selected-list-display-function="item => item.code"
                    :list-item-display-function="item => `${item.code} - ${item.service_nickname}`"
                ></vw-multi-select>
            </div>
        </div>

        <div class="form-group row align-items-center" v-if="sourceType !== 'insurance_provider'">
            <label for="service_codes" class="small col-3">*Service Codes:</label>
            <div class="col-8 px-0">
                <vw-multi-select
                    id="service_codes"
                    v-model="contract.service_codes"
                    :invalid="errors['service_codes']"
                    :styles="{ input: { width: '100%' } }"
                    :template="'bootstrap'"
                    :iconset="'font-awesome'"
                    :list="activeCodes"
                    :value-property="'code'"
                    :searchable="true"
                    :tags="true"
                    :selected-list-display-function="item => item.code"
                    :list-item-display-function="item => `${item.code} - ${item.service_nickname}`"
                ></vw-multi-select>
            </div>
        </div>

        <div class="form-group row align-items-center">
            <label for="identifier" class="col-3 small">*Identification number:</label>
            <input
                type="text"
                class="form-control form-control-sm col-2"
                id="identifier"
                name="identifier"
                v-model="contract.identifier"
                :class="errors['identifier'] ? 'border-danger' : ''"
                :placeholder="
                    sourceType === 'insurance_provider'
                        ? 'Member ID'
                        : 'Student ID, Authorization Number'
                "
            />

            <label for="authorization_number" class="col-2 offset-1 small"
                >Prior authorization number:</label
            >
            <input
                type="text"
                class="form-control form-control-sm col-2"
                id="authorization_number"
                name="authorization_number"
                v-model="contract.authorization_number"
                :class="errors['authorization_number'] ? 'border-danger' : ''"
            />
        </div>

        <div class="form-group row align-items-center" v-if="sourceType === 'insurance_provider'">
            <label for="address_name" class="col-3 small">
                Payer name:
                <i
                    class="fa fa-info-circle text-info"
                    v-tooltip="
                        `Some insurance providers (such as Magellan) require a payer address. Please check back of card for where to send claims.`
                    "
                ></i>
            </label>

            <input
                type="text"
                class="form-control form-control-sm col-2"
                id="address_name"
                name="address_name"
                v-model="contract.address_name"
                :class="errors['address_name'] ? 'border-danger' : ''"
                placeholder="Payer"
            />

            <label for="address1" class="col-2 offset-1 small">Payer address:</label>
            <input
                type="text"
                class="form-control form-control-sm col-2"
                id="address1"
                name="address1"
                v-model="contract.address1"
                :class="errors['address1'] ? 'border-danger' : ''"
                placeholder="PO Box"
            />
        </div>

        <div class="form-group row align-items-center" v-if="sourceType === 'insurance_provider'">
            <label for="city" class="col-3 small">Payer city:</label>
            <input
                type="text"
                class="form-control form-control-sm col-2"
                id="city"
                name="city"
                v-model="contract.city"
                :class="errors['city'] ? 'border-danger' : ''"
                placeholder="City"
            />

            <label for="state" class="col-2 offset-1 small">Payer state/zip:</label>
            <input
                type="text"
                class="form-control form-control-sm col-1"
                id="state"
                name="state"
                v-model="contract.state"
                :class="errors['state'] ? 'border-danger' : ''"
                placeholder="State"
            />
            <input
                type="text"
                class="form-control form-control-sm col-2"
                id="zip"
                name="zip"
                v-model="contract.zip"
                v-mask="'#####-####'"
                :class="errors['zip'] ? 'border-danger' : ''"
                placeholder="Zip"
            />
        </div>

        <div class="form-group row" v-if="sourceType !== 'insurance_provider'">
            <label for="is_copay_waiver" class="col-3 small">This is a copay waiver:</label>
            <input
                class="mr-2"
                v-model="contract.is_copay_waiver"
                type="checkbox"
                :false-value="0"
                :true-value="1"
                id="is_copay_waiver"
                name="is_copay_waiver"
                :class="errors['is_copay_waiver'] ? 'border-danger' : ''"
            />
        </div>

        <div class="form-group row" v-if="sourceType === 'insurance_provider'">
            <label for="rendering_therapist_id" class="col-3 small"
                >Rendering therapist:
                <i
                    class="fa fa-info-circle text-info"
                    v-tooltip="
                        `Don't see the therapists you're looking for? Make sure their 'NPI number' is filled in the Staff detail page.`
                    "
                ></i>
            </label>

            <div class="col-3 px-0">
                <select
                    name="service_codes"
                    id="rendering_therapist_id"
                    v-model="contract.rendering_therapist_id"
                    :class="errors['rendering_therapist_id'] ? 'border-danger' : ''"
                    class="form-control form-control-sm"
                >
                    <option :value="null">Choose one</option>
                    <option v-for="therapist in npiTherapists" :value="therapist.id">{{
                        therapist.name
                    }}</option>
                </select>
            </div>

            <label for="is_override_company_npi" class="col-3 small"
                >Override company NPI
                <i
                    class="text-info fa fa-info-circle"
                    v-tooltip="
                        'When checked, this NPI number will be used as the Company’s NPI as well as the Rendering Provider’s NPI. Payouts will be made to the Rendering therapist and NOT the company.'
                    "
                ></i
            ></label>

            <div class="col-1 pl-0">
                <input
                    class="mr-2"
                    :disabled="!contract.rendering_therapist_id"
                    v-model="contract.is_override_company_npi"
                    type="checkbox"
                    :false-value="0"
                    :true-value="1"
                    id="is_override_company_npi"
                    name="is_override_company_npi"
                />
            </div>
        </div>

        <div
            class="form-group row align-items-center"
            v-if="sourceType === 'insurance_provider' || contract.is_copay_waiver"
        >
            <label for="copay_value" class="col-3 small">*Copay:</label>

            <div class="input-group input-group-sm col-2 px-0">
                <div class="input-group-prepend">
                    <div class="input-group-text"><i class="fa fa-dollar"></i></div>
                </div>
                <input
                    id="copay_value"
                    name="copay_value"
                    type="text"
                    class="text-right form-control form-control-sm"
                    :class="errors['copay_value'] ? 'border-danger' : ''"
                    v-model.number="contract.copay_value"
                    placeholder=""
                />
            </div>

            <template v-if="sourceType === 'insurance_provider'">
                <label for="coinsurance_percentage" class="col-2 offset-1 small"
                    >Coinsurance %:</label
                >
                <input
                    id="coinsurance_percentage"
                    name="coinsurance_percentage"
                    type="text"
                    class="col-2 form-control form-control-sm"
                    :class="errors['coinsurance_percentage'] ? 'border-danger' : ''"
                    v-model.number="contract.coinsurance_percentage"
                    placeholder=""
                />
            </template>
        </div>

        <div class="form-group row">
            <label class="col-3 text-nowrap small">Contract Start Date:</label>
            <div class="col-2 p-0">
                <v-date-picker
                    class="picker-container"
                    id="start_at"
                    :class="errors['start_at'] ? 'border-danger' : ''"
                    popover-align="left"
                    v-model="contract.start_at"
                ></v-date-picker>
            </div>
            <label class="col-2 offset-1 text-nowrap small">*Contract End Date:</label>
            <div class="col-2 p-0">
                <v-date-picker
                    class="picker-container"
                    id="end_at"
                    :class="errors['end_at'] ? 'border-danger' : ''"
                    popover-align="left"
                    v-model="contract.end_at"
                ></v-date-picker>
            </div>
        </div>

        <div class="form-group row">
            <label class="col-3 text-nowrap small">Contract Approved:</label>
            <div class="col-2 p-0">
                <v-date-picker
                    class="picker-container"
                    id="approved_at"
                    :class="errors['approved_at'] ? 'border-danger' : ''"
                    popover-align="left"
                    v-model="contract.approved_at"
                ></v-date-picker>
            </div>
        </div>

        <div class="form-group row">
            <label class="small col-3">Next Report Due Date:</label>
            <div class="col-2 p-0">
                <v-date-picker
                    class="picker-container"
                    id="report_due_at"
                    :class="errors['report_due_at'] ? 'border-danger' : ''"
                    popover-align="left"
                    v-model="contract.report_due_at"
                ></v-date-picker>
            </div>
            <label class="col-2 offset-1 small">Next Reevaluation Date:</label>
            <div class="col-2 p-0">
                <v-date-picker
                    class="picker-container"
                    :class="errors['next_evaluation_at'] ? 'border-danger' : ''"
                    id="next_evaluation_at"
                    popover-align="left"
                    v-model="contract.next_evaluation_at"
                ></v-date-picker>
            </div>
        </div>

        <div class="form-group row align-items-center">
            <label class="col-3 small" for="approved_unit_type">Limit by:</label>
            <select
                name="approved_unit_type"
                id="approved_unit_type"
                v-model="contract.approved_unit_type"
                class="form-control form-control-sm col-2"
                :class="errors['approved_unit_type'] ? 'border-danger' : ''"
            >
                <option v-for="o in approvedUnitTypes" :value="o.simple">{{ o.pretty }}</option>
            </select>

            <label for="approved_used" class="col-2 offset-1 small"
                >{{ approvedUnitTypeLabel }} used:</label
            >
            <input
                id="approved_used"
                name="approved_used"
                type="text"
                :class="errors['approved_used'] ? 'border-danger' : ''"
                class="col-2 form-control form-control-sm"
                v-model="contract.approved_used"
                placeholder=""
            />
        </div>

        <div class="form-group row align-items-center">
            <label for="approved_max_weekly" class="col-3 small"
                >{{ approvedUnitTypeLabel }} limit weekly:</label
            >
            <input
                id="approved_max_weekly"
                name="approved_max_weekly"
                type="text"
                class="col-2 form-control form-control-sm"
                :class="errors['approved_max_weekly'] ? 'border-danger' : ''"
                v-model.number="contract.approved_max_weekly"
                placeholder=""
            />

            <label for="approved_max_monthly" class="col-2 offset-1 small"
                >{{ approvedUnitTypeLabel }} limit monthly:</label
            >
            <input
                id="approved_max_monthly"
                name="approved_max_monthly"
                type="text"
                class="col-2 form-control form-control-sm"
                :class="errors['approved_max_monthly'] ? 'border-danger' : ''"
                v-model.number="contract.approved_max_monthly"
                placeholder=""
            />
        </div>

        <div class="form-group row align-items-center">
            <label for="approved_max_total" class="col-3 small"
                >{{ approvedUnitTypeLabel }} limit total:</label
            >
            <input
                id="approved_max_total"
                name="approved_max_total"
                type="text"
                class="col-2 form-control form-control-sm"
                :class="errors['approved_max_total'] ? 'border-danger' : ''"
                v-model.number="contract.approved_max_total"
                placeholder=""
            />
        </div>

        <div class="form-group row align-items-center">
            <label for="options_days_week_seen" class="col-3 small">Days/week seen:</label>
            <input
                id="options_days_week_seen"
                name="options_days_week_seen"
                type="text"
                class="col-2 form-control form-control-sm"
                :class="errors['options.days_week_seen'] ? 'border-danger' : ''"
                v-model.number="contract.options.days_week_seen"
                placeholder=""
            />

            <label for="options_service_time" class="col-2 offset-1 small"
                >Service time limit:</label
            >
            <input
                id="options_service_time"
                name="options_service_time"
                type="text"
                class="col-1 form-control form-control-sm"
                :class="errors['options.service_time'] ? 'border-danger' : ''"
                v-model.number="contract.options.service_time"
                placeholder=""
            />
            <span class="small">&nbsp;minutes</span>
        </div>

        <div class="form-group row align-items-center" v-if="sourceType !== 'insurance_provider'">
            <label class="small col-3" for="invoice_interval">*Invoice interval</label>
            <select
                name="invoice_interval"
                id="invoice_interval"
                v-model="contract.invoice_interval"
                class="form-control form-control-sm col-2"
                :class="errors['invoice_interval'] ? 'border-danger' : ''"
            >
                <option selected disabled hidden :value="null">Choose one</option>
                <option v-for="o in invoiceIntervals" :value="o.simple">{{ o.pretty }}</option>
            </select>
        </div>

        <div class="d-flex justify-content-end mt-4 mb-3">
            <button
                class="btn btn-sm btn-primary text-uppercase font-weight-bold text-white"
                name="save-new-funding"
                id="save-new-funding"
                @click.prevent="savePatientFunding()"
            >
                Save
            </button>
        </div>

        <errors :errors="errors"></errors>
    </section>
</template>

<script>
import { fixDatePickers } from "../../plugins/fixDatePickers";
import { helpers } from "../../plugins/helpers";
import { errors } from "../../plugins/errors";

export default {
    name: "PatientFundingNew",
    mixins: [fixDatePickers, errors],
    props: [
        "patient",
        "fundingSources",
        "cptCodes",
        "serviceCodes",
        "diagnoses",
        "appointmentTypes",
    ],
    data() {
        return {
            multiselect: [],
            isEditMode: false,
            therapists: [],
            sourceType: null,
            sourceTypes: [
                { pretty: "Insurance Provider", simple: "insurance_provider" },
                { pretty: "School", simple: "school" },
                { pretty: "Regional Center", simple: "regional_center" },
                { pretty: "Easterseals", simple: "easter_seal" },
            ],
            funding: {
                source_id: null,
                contract: {},
            },
            contract: {
                address_name: null,
                address1: null,
                city: null,
                state: null,
                zip: null,
                appointment_type_id: null,
                rendering_therapist_id: null,
                is_override_company_npi: false,
                invoice_interval: null,
                start_at: null,
                end_at: null,
                report_due_at: null,
                next_evaluation_at: null,
                approved_at: null,
                approved_unit_type: "session",
                identifier: null,
                authorization_number: null,
                subscriber_first_name: null,
                subscriber_last_name: null,
                subscriber_relationship: null,
                cpt_code_ids: [],
                diagnoses: [],
                service_codes: [],
                is_copay_waiver: 0,
                copay_value: 0,
                coinsurance_percentage: null,
                approved_max_total: null,
                approved_max_weekly: null,
                approved_max_monthly: null,
                approved_used: null,
                service_time: null,
                options: {
                    days_week_seen: null,
                },
            },
            invoiceIntervals: [
                { pretty: "Session", simple: "session" },
                { pretty: "Weekly", simple: "weekly" },
                { pretty: "Biweekly", simple: "biweekly" },
                { pretty: "Monthly", simple: "monthly" },
            ],
            baseApprovedUnitTypes: [
                { pretty: "Minutes", simple: "minute" },
                { pretty: "Sessions", simple: "session" },
            ],
            method: "POST",
        };
    },
    created() {
        this.fetchTherapists();
    },

    watch: {
        "contract.is_copay_waiver"() {
            if (!this.contract.is_copay_waiver) {
                this.contract.copay_value = null;
            }
        },
        sourceType() {
            setTimeout(this.removePickerMinWidth, 1);
        },
    },

    computed: {
        filteredFundingSources() {
            let sourcesWithExpectedPayments = this.fundingSources.filter(
                source => Object.keys(source.expected_payments).length
            );
            if (this.sourceType) {
                return sourcesWithExpectedPayments.filter(s => s.type === this.sourceType);
            }
            return sourcesWithExpectedPayments;
        },

        approvedUnitTypes() {
            let types = [...this.baseApprovedUnitTypes];
            if (this.sourceType !== "insurance_provider") {
                types.push({ pretty: "Budget ($)", simple: "amount" });
            }
            return types;
        },

        approvedUnitTypeLabel() {
            return this.approvedUnitTypes.filter(
                s => s.simple === this.contract.approved_unit_type
            )[0].pretty;
        },

        patientId() {
            return this.patient.id;
        },

        npiTherapists() {
            if (this.therapists && this.therapists.length) {
                return this.therapists.filter(therapist => therapist.npi_number);
            }
            return [];
        },

        activeCodes() {
            if (this.funding.source_id) {
                const selectedSource = this.fundingSources.find(
                    source => source.id === this.funding.source_id
                );
                if (selectedSource) {
                    const expectedPayments = Object.keys(selectedSource.expected_payments).map(
                        key => {
                            return {
                                cpt_code_id: key,
                                minutes: _.get(
                                    selectedSource,
                                    `expected_payments.${key}.minutes`,
                                    null
                                ),
                                charge_rate: _.get(
                                    selectedSource,
                                    `expected_payments.${key}.charge_rate`,
                                    null
                                ),
                                expected_rate: _.get(
                                    selectedSource,
                                    `expected_payments.${key}.expected_rate`,
                                    null
                                ),
                            };
                        }
                    );
                    const activeCodes = expectedPayments
                        .filter(ep => ep.charge_rate && ep.expected_rate && ep.minutes)
                        .map(filtered => parseInt(filtered.cpt_code_id));
                    const allCodes = [...this.cptCodes, ...this.serviceCodes];
                    return allCodes.filter(code => activeCodes && activeCodes.includes(code.id));
                }
            }
            return [];
        },
    },

    methods: {
        copyFunding() {
            this.$emit("copy-funding");
            this.isEditMode = false;
            this.method = "POST";
            delete this.contract.id;
            delete this.contract.approved_used;
            this.contract.deductible_amount_met = 0;
            this.contract.max_out_of_pocket_amount_met = 0;
            this.contract.cpt_codes = [];
            this.contract.service_codes = [];
        },

        fetchTherapists() {
            axios
                .get(`/api/organizations/${this.$root.organization.id}/therapists`)
                .then(r => {
                    this.therapists = r.data.data;
                })
                .catch(error => {
                    console.error(error);
                });
        },

        preparePayload() {
            let contract = { ...this.contract };
            contract.start_at = contract.start_at
                ? this.moment(contract.start_at).format("YYYY-MM-DD")
                : null;
            contract.end_at = contract.end_at
                ? this.moment(contract.end_at).format("YYYY-MM-DD")
                : null;
            contract.report_due_at = contract.report_due_at
                ? this.moment(contract.report_due_at).format("YYYY-MM-DD")
                : null;
            contract.next_evaluation_at = contract.next_evaluation_at
                ? this.moment(contract.next_evaluation_at).format("YYYY-MM-DD")
                : null;
            contract.approved_at = contract.approved_at
                ? this.moment(contract.approved_at).format("YYYY-MM-DD")
                : null;
            contract.eligibility_updated_at = contract.eligibility_updated_at
                ? this.moment(contract.eligibility_updated_at).format("YYYY-MM-DD")
                : null;
            contract.service_codes = [...new Set(contract.service_codes)];
            if (isNaN(contract.copay_value)) {
                contract.copay_value = 0;
            }
            let payload = { ...this.funding };
            payload = helpers.prepareCodesPayload(payload);

            switch (this.method) {
                case "POST":
                    payload.contract = contract;
                    return payload;
                case "PATCH":
                    return contract;
            }
        },

        apiUrl() {
            switch (this.method) {
                case "POST":
                    return `/api/patients/${this.patientId}/fundings`;
                case "PATCH":
                    return `/api/patients/${this.patientId}/fundings/${this.funding.id}/contracts/${
                        this.contract.id
                    }`;
            }
        },

        savePatientFunding() {
            this.setErrors();
            helpers.toggleSpinner("save-new-funding");
            const payload = this.preparePayload();
            axios({
                url: this.apiUrl(),
                method: this.method,
                data: payload,
            })
                .then(r => {
                    helpers.toggleSpinner("save-new-funding");
                    this.$emit("new-entry");
                    this.$emit("close");
                    this.cleanup();
                })
                .catch(e => {
                    this.setErrors(_.get(e, "response.data.errors", {}));
                    helpers.toggleSpinner("save-new-funding");
                    console.error(e);
                });
        },

        setEdit(funding, contract) {
            this.isEditMode = true;
            this.funding = { ...funding };
            this.treatNestedObjects(this.funding);
            this.sourceType = this.funding.source.type;
            this.contract = { ...contract };
            this.treatNestedObjects(this.contract);
            ["diagnoses", "service_codes"].forEach(prop => {
                // Treat CPT and Diagnoses to...
                let itemCodes = []; //
                this.contract[prop].forEach(item => {
                    //
                    itemCodes.push(item.code); // replace objects with codes
                });
                this.contract[prop] = itemCodes;
            });

            if (!this.contract.options) {
                this.contract.options = {
                    days_week_seen: null,
                };
            }
            if (isNaN(contract.copay_value)) {
                contract.copay_value = 0;
            }
            this.method = "PATCH";
        },

        treatNestedObjects(obj) {
            Object.keys(obj).forEach(k => {
                if (obj[k] && obj[k].hasOwnProperty("id")) {
                    //converts camelCase object to snake_case_id
                    const kSnake = k.replace(/([A-Z])/g, "_$1").toLowerCase();
                    obj[kSnake + "_id"] = obj[k].id;
                }
                if (k.slice(-2) === "at") {
                    //converts dates to JavaScript dates
                    obj[k] = obj[k] ? new Date(obj[k]) : obj[k];
                }
            });
        },

        cleanup() {
            this.setErrors();
            this.isEditMode = false;
            this.method = "POST";
            this.funding = {};
            this.sourceType = null;
            this.contract = {
                address_name: null,
                address1: null,
                city: null,
                state: null,
                zip: null,
                appointment_type_id: null,
                rendering_therapist_id: null,
                is_override_company_npi: false,
                invoice_interval: null,
                start_at: null,
                end_at: null,
                report_due_at: null,
                next_evaluation_at: null,
                approved_at: null,
                identifier: null,
                authorization_number: null,
                subscriber_first_name: null,
                subscriber_last_name: null,
                subscriber_relationship: null,
                cpt_codes: [],
                diagnoses: [],
                service_codes: [],
                is_copay_waiver: 0,
                copay_value: 0,
                coinsurance_percentage: null,
                approved_unit_type: "session",
                approved_used: null,
                approved_max_weekly: null,
                approved_max_monthly: null,
                approved_max_total: null,
                service_time: null,
                options: {
                    days_week_seen: null,
                },
            };
            this.funding = {
                source_id: null,
                contract: {},
            };
        },
    },
};
</script>

<style scoped lang="scss">
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
</style>
